<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import SaleService from '@/services/sale';
import ProductService from '@/services/product';
import { productColor, productDescription } from '@/utils/product';

export default {
  page: {
    title: "Pedido",
    meta: [{ name: "description", content: appConfig.description }]
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "Pedido",
      saleId: '',
      sale: {
        customer: {},
        shipping: {
          address: {}
        },
        payment: {
          payment_option: {},
        },
        coupon: {},
      },

      editingItems: [],
      hasChanges: false,

      productSearchTerm: '',
      productSearchResults: [],
      productSearchLoading: false,
      addingProduct: null,
      addQuantity: 1,

      statusOptions: [
        { code: 'new', description: 'Novo' },
        { code: 'confirmed', description: 'Confirmado' },
        { code: 'separation', description: 'Em Separação' },
        { code: 'sent', description: 'Enviado' },
        { code: 'ready', description: 'Pronto p/ Retirada' },
        { code: 'delivered', description: 'Entregue' },
        { code: 'completed', description: 'Finalizado' },
      ],

      statusOptionsDisabled: [
        { code: 'awaiting_payment', description: 'Aguardando Pagamento' },
        { code: 'canceled', description: 'Cancelado' },
      ]
    };
  },
  computed: {
    tableFields() {
      const base = [
        { key: 'image', label: 'Produto' },
        { key: 'product.code', label: 'Cód.', formatter: (_value, _index, item) => {
          return item.product.code || item.product.sku || item.product.barcode
        } },
        { key: 'description', label: 'Descrição' },
        { key: 'price', label: 'Preço', formatter: (value) => {
          return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        } },
        { key: 'quantity', label: 'Qtde.' },
        { key: 'subtotal', label: 'Subtotal' },
      ];
      if (this.sale.status === 'new') {
        base.push({ key: 'actions', label: '' });
      }
      return base;
    },
  },
  mounted() {
    this.saleId = this.$route.params.id;

    this.load();

    this.connectSocket();
  },
  sockets: {
    updateSale(data) {
      if (data.saleId && data.saleId === this.sale._id) {
        this.load(false);
      }
    },
  },
  methods: {
    productColor,
    productDescription,
    connectSocket() {
      this.$store.dispatch('USER_DATA').then((res) => {
        this.user = res;

        this.$socket.emit('subscribe', this.user.user_id);
      })
    },
    load(showLoading = true) {
      let loader;
      if (showLoading) {
        loader = this.$loading.show();
      }

      SaleService.getSale(this.saleId).then(res => {
        if (loader) {
          loader.hide();
        }

        this.sale = res.data;
        this.sale.oldStatus = this.sale.status;
        this.title = `Pedido #${this.sale.code}`;

        this.sale.items = this.sale.items.map(item => {
          return {
            ...item,
            image: item.product.images[0] ? item.product.images[0].url : 'https://bizmob.s3.amazonaws.com/images/default_image.png',
          }
        });

        this.loadEditingItems();

        if (!showLoading) {
          this.$forceUpdate();
        }
      }).catch(() => {
        if (loader) {
          loader.hide();
        }
      })
    },
    loadEditingItems() {
      this.editingItems = this.sale.items.map(item => ({ ...item }));
      this.hasChanges = false;
    },
    removeItem(index) {
      this.editingItems.splice(index, 1);
      this.hasChanges = true;
    },
    onQuantityChange(index, value) {
      const qty = parseFloat(value);
      if (!isNaN(qty) && qty > 0) {
        this.editingItems[index].quantity = qty;
        this.hasChanges = true;
      }
    },
    cancelEditing() {
      this.loadEditingItems();
    },
    async saveItems() {
      const loader = this.$loading.show();
      try {
        const items = this.editingItems.map(item => ({
          product: item.product._id || item.product,
          quantity: item.quantity,
        }));

        const res = await SaleService.updateItems(this.sale._id, items);
        this.$toast.open('Itens do pedido atualizados');
        if (res && res.data && res.data.warning) {
          this.$toast.open({ message: res.data.warning, type: 'warning', duration: 6000 });
        }
        this.load(false);
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.message;
        this.$toast.error(msg || 'Erro ao salvar itens');
      } finally {
        loader.hide();
      }
    },
    openAddProduct() {
      this.productSearchTerm = '';
      this.productSearchResults = [];
      this.addingProduct = null;
      this.addQuantity = 1;
      this.$bvModal.show('modal-add-product');
    },
    searchProducts() {
      if (!this.productSearchTerm.trim()) return;
      this.productSearchLoading = true;
      ProductService.getAllProducts(1, this.productSearchTerm).then(res => {
        const data = res.data;
        this.productSearchResults = Array.isArray(data) ? data : (data.data || data.products || []);
      }).finally(() => {
        this.productSearchLoading = false;
      });
    },
    selectProduct(product) {
      this.addingProduct = product;
      this.addQuantity = 1;
    },
    confirmAddProduct() {
      if (!this.addingProduct || this.addQuantity <= 0) return;

      const existingIndex = this.editingItems.findIndex(i => {
        const id = i.product._id || i.product;
        return String(id) === String(this.addingProduct._id);
      });

      if (existingIndex >= 0) {
        this.editingItems[existingIndex].quantity += Number(this.addQuantity);
      } else {
        this.editingItems.push({
          product: this.addingProduct,
          quantity: Number(this.addQuantity),
          price: this.addingProduct.price,
          image: this.addingProduct.images && this.addingProduct.images[0]
            ? this.addingProduct.images[0].url
            : 'https://bizmob.s3.amazonaws.com/images/default_image.png',
          complements: [],
        });
      }

      this.hasChanges = true;
      this.$bvModal.hide('modal-add-product');
    },
    formatDate(value, time = true) {
      if (value) {
        if (time) {
          return this.$moment(value).format('DD/MM/YYYY HH:mm:ss');
        }

        return this.$moment(value).format('DD/MM/YYYY');
      }

      return '-';
    },
    changeStatus() {
      this.$bvModal.hide('modal-confirm');

      let loader = this.$loading.show();
      SaleService.changeStatus(this.sale._id, this.sale.status).then(() => {
        loader.hide();

        this.$toast.open('Status do pedido alterado');

        this.load(false);
      }).catch(() => {
        loader.hide();
      })
    },
    cancelSale() {
      this.$bvModal.hide('modal-cancel');

      let loader = this.$loading.show();
      SaleService.changeStatus(this.sale._id, 'canceled').then(() => {
        loader.hide();

        this.$toast.open('Pedido cancelado');

        this.load(false);
      }).catch(() => {
        loader.hide();
      })
    },
    changeStatusMessage() {
      const status = this.statusOptions.find(item => item.code === this.sale.status);
      if (status) {
        return status.description;
      }
    }
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title"/>

    <div class="row">
      <div class="col-xl-8">
        <div class="card">
          <div class="card-body">
            <div class="table-responsive">
              <b-table
                align="center"
                :items="editingItems"
                :fields="tableFields"
                responsive="sm"
                head-variant="light"
              >
                <template v-slot:cell(image)="row">
                  <img
                    v-bind:src="row.item.image"
                    alt="product-img"
                    title="product-img"
                    class="avatar-md"
                  />
                </template>

                <template v-slot:cell(description)="row">
                  <p class="mb-0">{{row.item.product.description}}</p>
                  <p class="text-muted font-size-11 mb-0" v-if="productColor(row.item.product)">Cor: {{productColor(row.item.product)}}</p>
                  <p class="text-muted font-size-11 mb-0" v-for="(complement, index) in row.item.complements" :key="index">
                    {{complement.quantity}}x {{complement.description}}
                  </p>
                  <p class="text-muted font-size-11 mb-0" v-if="row.item.note">Obs.: {{row.item.note}}</p>
                </template>

                <template v-slot:cell(quantity)="row">
                  <b-form-input
                    v-if="sale.status === 'new'"
                    type="number"
                    :value="row.item.quantity"
                    @change="onQuantityChange(row.index, $event)"
                    min="1"
                    step="1"
                    size="sm"
                    style="width: 80px"
                  />
                  <span v-else>{{ row.item.quantity }}</span>
                </template>

                <template v-slot:cell(subtotal)="row">
                  {{ ((row.item.quantity * row.item.price) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
                </template>

                <template v-slot:cell(actions)="row">
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="removeItem(row.index)"
                    title="Remover item"
                  >
                    <i class="mdi mdi-delete"></i>
                  </b-button>
                </template>
              </b-table>
            </div>

            <div v-if="sale.status === 'new'" class="mt-3 d-flex align-items-center justify-content-between">
              <b-button variant="outline-primary" size="sm" @click="openAddProduct">
                <i class="mdi mdi-plus mr-1"></i> Adicionar Produto
              </b-button>

              <div v-if="hasChanges">
                <b-button variant="link" size="sm" class="mr-2" @click="cancelEditing">Descartar</b-button>
                <b-button variant="success" size="sm" @click="saveItems">
                  <i class="mdi mdi-content-save mr-1"></i> Salvar Alterações
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-sm-6">
            <b-button variant="link" @click="$router.go(-1)"><i class="mdi mdi-arrow-left mr-1"></i> Voltar para lista de pedidos</b-button>
          </div>
        </div>
      </div>
      <div class="col-xl-4">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">Status</h4>

            <div class="row">
              <div class="col col-8">
                <div class="form-group">
                  <input disabled  type="text" class="form-control" v-model="sale.statusDescription" />
                </div>
              </div>

              <div class="col pl-0">
                <b-button
                  class="status-button"
                  block
                  variant="primary"
                  @click="$bvModal.show('modal-confirm')"
                  :disabled="sale.status === 'canceled' || (sale.status === 'awaiting_payment' && sale.payment.method !== 'pix_offline')"
                >Alterar</b-button>
              </div>
            </div>

            <b-button
              class="status-button ml-0"
              block
              variant="outline-danger"
              v-if="sale.status !== 'canceled' && (sale.status !== 'awaiting_payment' || sale.payment.method === 'pix_offline')"
              @click="$bvModal.show('modal-cancel')"
            >Cancelar Pedido</b-button>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">Dados do Cliente</h4>

            <div class="table-responsive mb-0">
              <table class="table mb-0">
                <tbody>
                  <tr>
                    <td>Nome:</td>
                    <td>{{sale.customer.name}}</td>
                  </tr>

                  <tr>
                    <td>CPF:</td>
                    <td>{{sale.customer.document_number}}</td>
                  </tr>

                  <tr>
                    <td>E-mail:</td>
                    <td>{{sale.customer.email}}</td>
                  </tr>

                  <tr>
                    <td>Telefone:</td>
                    <td>{{sale.customer.phone_number}}</td>
                  </tr>

                  <tr v-if="sale.customer.birthdate">
                    <td>Data de Nascimento:</td>
                    <td>{{sale.customer.birthdate}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">Forma de Entrega</h4>

            <div class="table-responsive mb-0">
              <table class="table mb-0">
                <tbody v-if="sale.shipping.method === 'withdraw'">
                  <tr>
                    <td>Retirada na loja</td>
                  </tr>
                </tbody>

                <tbody v-if="sale.shipping.method === 'own'">
                  <tr>
                    <td>Entrega própria</td>
                  </tr>
                </tbody>

                <tbody v-if="sale.shipping.method === 'delivery'">
                  <tr v-if="sale.shipping.carrier">
                    <td>Transportadora:</td>
                    <td>{{sale.shipping.carrier}}</td>
                  </tr>

                  <tr v-if="sale.shipping.service_description">
                    <td>Serviço:</td>
                    <td>{{sale.shipping.service_description}}</td>
                  </tr>

                  <tr v-if="sale.shipping.time">
                    <td>Prazo:</td>
                    <td>{{sale.shipping.time}} dia{{ sale.shipping.time != 1 ? 's' : '' }} útil{{ sale.shipping.time != 1 ? 'eis' : '' }}</td>
                  </tr>

                  <tr v-if="sale.shipping.schedule && sale.shipping.schedule.date">
                    <td>Agendado:</td>
                    <td>{{formatDate(sale.shipping.schedule.date, false)}} {{sale.shipping.schedule.description}}</td>
                  </tr>

                  <tr>
                    <td>Endereço:</td>
                    <td>{{sale.shipping.address.street}}, {{sale.shipping.address.number}} {{sale.shipping.address.complement ? ' - ' : ''}} {{sale.shipping.address.complement || ''}}</td>
                  </tr>

                  <tr>
                    <td>Bairro:</td>
                    <td>{{sale.shipping.address.district}}</td>
                  </tr>

                  <tr>
                    <td>Cidade:</td>
                    <td>{{sale.shipping.address.city}}/{{sale.shipping.address.state}}</td>
                  </tr>

                  <tr>
                    <td>CEP:</td>
                    <td>{{sale.shipping.address.zipcode}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">Forma de Pagamento</h4>

            <div class="table-responsive mb-0">
              <table class="table mb-0">
                <tbody>
                  <tr>
                    <td>
                      {{sale.payment.payment_option.description}}
                      {{sale.payment.change ? `(troco para ${sale.payment.change.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) })` : ''}}
                      {{sale.payment.installments && sale.payment.installments > 1 ? ` em ${sale.payment.installments}x` : ''}}
                    </td>
                  </tr>
                  <tr v-if="!sale.payment.captured && sale.payment.captured_message">
                    <td>
                      {{sale.payment.captured_message}}
                      <span class="badge badge-pill badge-soft-danger font-size-12 ml-2">Não Capturado</span>
                    </td>
                  </tr>
                  <tr v-if="sale.payment.captured">
                    <td>
                      Valor: {{sale.payment.captured_value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}}
                      <span class="badge badge-pill badge-soft-success font-size-12 ml-2">Capturado</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">Total do Pedido</h4>

            <div class="table-responsive">
              <table class="table mb-0">
                <tbody>
                  <tr>
                    <td>Produtos :</td>
                    <td>{{(sale.total_products || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}}</td>
                  </tr>
                  <tr>
                    <td>Taxa de Entrega :</td>
                    <td>{{(sale.shipping.price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}}</td>
                  </tr>
                  <tr v-if="sale.discount_value">
                    <td>Desconto :</td>
                    <td>
                      - {{(sale.discount_value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}}
                      <p class="mb-0" v-if="sale.coupon && sale.coupon.code">Cupom:  {{sale.coupon.code}}</p>
                    </td>
                  </tr>
                  <tr class="font-size-18">
                    <th>Total :</th>
                    <th>{{(sale.total_value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}}</th>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="modal-confirm"
      title="Alteração de Status"
      title-class="font-18"
      centered
      size="sm"
    >
      <p>Selecione o novo status do pedido:</p>

      <div class="row">
        <div class="col">
          <select class="form-control" v-model="sale.status">
            <option
              v-for="status in statusOptions"
              :key="status.code"
              :value="status.code"
            >{{status.description}}</option>
          </select>
        </div>
      </div>

      <div slot="modal-footer">
        <b-btn variant="link" @click="$bvModal.hide('modal-confirm')" class="mr-2">Voltar</b-btn>
        <b-btn variant="primary" @click="changeStatus">Confirmar</b-btn>
      </div>
    </b-modal>

    <b-modal
      id="modal-cancel"
      title="Cancelar Pedido"
      title-class="font-18"
      centered
    >
      <p>Confirma o cancelamento do pedido?</p>

      <div slot="modal-footer">
        <b-btn variant="link" @click="$bvModal.hide('modal-cancel')" class="mr-2">Desistir</b-btn>
        <b-btn variant="primary" @click="cancelSale">Cancelar Pedido</b-btn>
      </div>
    </b-modal>

    <b-modal
      id="modal-add-product"
      title="Adicionar Produto"
      title-class="font-18"
      centered
      size="lg"
    >
      <div class="form-group">
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            placeholder="Buscar produto por descrição..."
            v-model="productSearchTerm"
            @keyup.enter="searchProducts"
          />
          <div class="input-group-append">
            <b-button variant="primary" @click="searchProducts" :disabled="productSearchLoading">
              <i class="mdi mdi-magnify"></i>
            </b-button>
          </div>
        </div>
      </div>

      <div v-if="productSearchLoading" class="text-center py-3">
        <b-spinner small></b-spinner> Buscando...
      </div>

      <div v-else-if="productSearchResults.length > 0">
        <div
          v-for="product in productSearchResults"
          :key="product._id"
          class="product-search-item d-flex align-items-center p-2 mb-2"
          :class="{ 'selected': addingProduct && addingProduct._id === product._id }"
          @click="selectProduct(product)"
          style="cursor: pointer; border: 1px solid #e8e8e8; border-radius: 4px;"
        >
          <img
            :src="product.images && product.images[0] ? product.images[0].url : 'https://bizmob.s3.amazonaws.com/images/default_image.png'"
            style="width: 48px; height: 48px; object-fit: cover; border-radius: 4px;"
            class="mr-3"
          />
          <div class="flex-grow-1">
            <p class="mb-0 font-weight-medium">{{ product.description }}</p>
            <small class="text-muted d-block" v-if="productColor(product)">Cor: {{ productColor(product) }}</small>
            <small class="text-muted">{{ product.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</small>
          </div>
          <i v-if="addingProduct && addingProduct._id === product._id" class="mdi mdi-check-circle text-success font-size-18"></i>
        </div>

        <div v-if="addingProduct" class="mt-3 p-3" style="background: #f8f9fa; border-radius: 4px;">
          <label class="font-weight-medium mb-1">Quantidade:</label>
          <div class="d-flex align-items-center" style="gap: 8px;">
            <b-button variant="outline-secondary" size="sm" @click="addQuantity = Math.max(1, addQuantity - 1)">
              <i class="mdi mdi-minus"></i>
            </b-button>
            <b-form-input
              type="number"
              v-model.number="addQuantity"
              min="1"
              step="1"
              size="sm"
              style="width: 80px; text-align: center;"
            />
            <b-button variant="outline-secondary" size="sm" @click="addQuantity++">
              <i class="mdi mdi-plus"></i>
            </b-button>
          </div>
        </div>
      </div>

      <p v-else-if="productSearchTerm && !productSearchLoading" class="text-muted text-center py-3">
        Nenhum produto encontrado.
      </p>

      <div slot="modal-footer">
        <b-btn variant="link" @click="$bvModal.hide('modal-add-product')" class="mr-2">Cancelar</b-btn>
        <b-btn variant="primary" @click="confirmAddProduct" :disabled="!addingProduct || addQuantity <= 0">Adicionar</b-btn>
      </div>
    </b-modal>
  </Layout>
</template>

<style lang="scss">
  .inactive {
    .text-primary, .text-success, h5, .text-muted {
      color: #dedede !important;
    }
  }

  .button-items {
    display: flex;

    .form-control {
      flex: 1 0;
    }

    .status-button {
      flex: 0 1;
    }
  }

  .table td {
    vertical-align: middle;
  }

  .product-search-item {
    transition: background 0.15s;

    &:hover {
      background: #f0f4ff;
    }

    &.selected {
      border-color: #556ee6 !important;
      background: #f0f4ff;
    }
  }
</style>
